import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { updateUser } from '@/actions/userActions'
import { filterNulls, useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useSignupPageUpdate } from '@/services/onboardingService'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useAnalyticsView } from '@/features/amplitude'
import { SIGNUP_PATHS } from '../helpers'
import {
  ReferralStatus,
  updateUserReferralStatusIfExists,
} from '@/featuresV1/Referrals/referrals.slice'
import { SignupHeader } from '../SignupHeader'

import { TypographyStyles } from '@/styles'
import {
  Button,
  Card,
  CardColors,
  Image,
  Link,
  List,
  Separator,
  Text,
} from '@/ui'

export const DisqualifiedSignup = () => {
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()
  const [searchParams] = useSearchParams()
  const currentUser = useReselector(getCurrentUser)
  const { email } = currentUser || {}
  useSignupPageUpdate(SIGNUP_PATHS.disqualified)

  useEffect(() => {
    pageView('sign up disqualifed')
  }, [pageView])

  useEffect(() => {
    updateUser(currentUser?.id, {
      disqualifiedAt: new Date().toISOString(),
    })(dispatch)

    if (email) {
      updateUserReferralStatusIfExists({
        recipientEmail: email,
        status: ReferralStatus.ineligible,
      })(dispatch)
    }
  }, [email, dispatch, currentUser?.id])

  const isScorpDisqualified = searchParams.get('ccorp') === 'true'
  const hasInternationalTaxes =
    searchParams.get('hasInternationalTaxes') === 'true'
  const hasMultipleOwners = searchParams.get('multipleOwners') === 'true'
  const hasSharedAccounts = searchParams.get('sharedAccounts') === 'true'
  const hasOtherSelection = searchParams.get('otherSelection') === 'true'

  const disqReasons = [
    hasInternationalTaxes ? 'International taxes' : null,
    hasMultipleOwners ? 'Multiple practice owners' : null,
    isScorpDisqualified ? 'C Corporation taxes' : null,
    hasSharedAccounts ? 'Combined personal and business banking' : null,
    hasOtherSelection
      ? 'We need to understand your business and tax entities before working with you'
      : null,
  ]

  return (
    <div className="container mx-auto pt-16">
      <SignupHeader hideProgress />

      <div className="flex flex-col space-y-4 items-center">
        <Card
          color={CardColors.LEVEL_1}
          className="flex flex-col items-center space-y-4 w-7/8 mx-4"
        >
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/seedling.svg"
            alt="seedling"
            height={170}
            width={120}
          />

          <Text variant={TypographyStyles.DISPLAY_MD}>
            We&apos;re not ready for you yet...
          </Text>

          <Text variant={TypographyStyles.BODY_MD} textAlign="center">
            Based on your answers, we aren&apos;t a good match for you at the
            moment, and won&apos;t be able to support what your practice needs
            for:
          </Text>

          <List className="w-1/2 mx-4 list-disc">
            {filterNulls(disqReasons).map((reason) => (
              <List.Item key={reason}>
                <Text>{reason}</Text>
              </List.Item>
            ))}
          </List>

          {isScorpDisqualified && (
            <Card color={CardColors.WARNING} className="w-1/2 mx-4">
              <Text variant={TypographyStyles.BODY_MD} className="p-4">
                We are currently only able to support C Corporation (including
                Professional Corporation) business entities that are taxed as S
                Corporations for the entire tax year. We hope to support C
                Corporation tax entities transitioning to S Corporation tax
                entities in the near future. Please keep an eye out for future
                offerings from Heard!
              </Text>
            </Card>
          )}

          <Text variant={TypographyStyles.BODY_MD} textAlign="center">
            If you feel like you were incorrectly disqualified, you can reach
            out to hello@joinheard.com
          </Text>
          <br />
          <Separator />

          <br />
          <Text variant={TypographyStyles.H1}>Keep in touch</Text>

          <Text
            variant={TypographyStyles.BODY_MD}
            textAlign="center"
            className="w-2/3 mx-8"
          >
            If your circumstances change in the future, please reach out again.
            In the meantime, we&apos;d love you to subscribe to our newsletter
            to keep up to date on our offerings, and get valuable tips for how
            to run your practice.
          </Text>

          <div className="flex flex-col items-center space-y-4 mt-12 w-1/2 gap-4">
            <Button
              className="grow max-w-md w-full"
              variant="primary"
              onClick={() =>
                window.open('https://www.joinheard.com/newsletter', '_blank')
              }
            >
              Subscribe
            </Button>

            <Button
              variant="secondary"
              asChild
              className="grow max-w-md w-full"
            >
              <Link
                href="https://www.joinheard.com"
                id="btn-signup-disqualified"
              >
                Return to Site
              </Link>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}
